/* ROVE & PACE — progressive enhancement. All primary forms work without JavaScript. */
(() => {
  'use strict';
  if (window.__rovePaceInitialized) return;
  window.__rovePaceInitialized = true;
  document.documentElement.classList.add('js');

  const config = () => window.RovePace || {};
  const routes = () => config().routes || {};
  const text = (key, fallback) => config().strings?.[key] || fallback;
  const roots = new WeakMap();
  const mediaMaps = new WeakMap();
  const busyForms = new WeakSet();
  const searches = new WeakMap();
  const dialogTriggers = new WeakMap();
  const modelViewers = new WeakSet();
  let modelFeature;
  let previousOverflow;

  function apiUrl(route, suffix = '.js') {
    const url = new URL(route, window.location.origin);
    if (!url.pathname.endsWith(suffix)) url.pathname += suffix;
    return url;
  }

  function money(cents) {
    const currency = config().currency || document.documentElement.dataset.currency || 'USD';
    const locale = document.documentElement.lang || 'en';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(cents) / 100);
    } catch (_) {
      return `${currency} ${(Number(cents) / 100).toFixed(2)}`;
    }
  }

  function announce(message) {
    document.querySelectorAll('[data-cart-status]').forEach(el => { el.textContent = message; });
  }

  function variantsFor(root) {
    if (roots.has(root)) return roots.get(root);
    let variants = [];
    try {
      const data = JSON.parse(root.querySelector('[data-variants-json]')?.textContent || '[]');
      if (Array.isArray(data)) variants = data;
    } catch (_) { /* Keep the server-rendered form usable if data is absent. */ }
    roots.set(root, variants);
    return variants;
  }

  function optionValues(root) {
    const values = [];
    root.querySelectorAll('select[data-option-index], input[data-option-index]:checked').forEach(input => {
      values[Number(input.dataset.optionIndex)] = input.value;
    });
    return values;
  }

  function variantMediaFor(root) {
    if (mediaMaps.has(root)) return mediaMaps.get(root);
    let map = {};
    try {
      const data = JSON.parse(root.querySelector('[data-variant-media-json]')?.textContent || '{}');
      if (data && typeof data === 'object' && !Array.isArray(data)) map = data;
    } catch (_) { /* The ordinary featured-media fallback remains usable. */ }
    mediaMaps.set(root, map);
    return map;
  }

  function selectedVariant(root) {
    const variants = variantsFor(root);
    const values = optionValues(root);
    if (values.length) return variants.find(variant => variant.options?.every((value, index) => value === values[index]));
    const id = root.querySelector('[data-variant-id]')?.value;
    return variants.find(variant => String(variant.id) === String(id)) || variants[0];
  }

  function showMedia(root, target) {
    const media = [...root.querySelectorAll('[data-media-id]')];
    const selected = media.find(item => String(item.dataset.mediaId) === String(target));
    if (!selected) return;
    media.forEach(item => {
      const active = item === selected;
      item.hidden = !active;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-hidden', String(!active));
      if (!active) item.querySelectorAll('video').forEach(video => video.pause());
    });
    root.querySelectorAll('[data-media-target]').forEach(button => {
      const active = String(button.dataset.mediaTarget) === String(target);
      if (button instanceof HTMLButtonElement) button.setAttribute('aria-pressed', String(active));
      else if (active) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
      button.classList.toggle('is-active', active);
    });
  }

  function renderVariant(root, updateUrl = false) {
    const variants = variantsFor(root);
    if (!variants.length) return;
    const variant = selectedVariant(root);
    const form = root.querySelector('[data-product-form]');
    const id = form?.querySelector('[data-variant-id]') || root.querySelector('[data-variant-id]');
    if (id) {
      const nextId = variant ? String(variant.id) : '';
      const changed = id.value !== nextId;
      id.value = nextId;
      id.disabled = !variant;
      if (changed) id.dispatchEvent(new Event('change', { bubbles: true }));
    }
    root.querySelectorAll('[data-add-to-cart]').forEach(button => {
      button.disabled = !variant?.available || !!(form && busyForms.has(form));
      const label = button.querySelector('[data-add-to-cart-label]') || button;
      label.textContent = form && busyForms.has(form)
        ? text('adding', 'Adding…')
        : !variant ? text('unavailable', 'Unavailable')
          : variant.available ? text('addToCart', 'Add to bag') : text('soldOut', 'Sold out');
    });
    if (!variant) {
      root.dataset.onSale = 'false';
      root.querySelectorAll('[data-product-compare]').forEach(el => { el.hidden = true; });
      return;
    }
    root.querySelectorAll('[data-product-price]').forEach(el => { el.textContent = money(variant.price); });
    const onSale = Number(variant.compare_at_price) > Number(variant.price);
    root.dataset.onSale = String(onSale);
    root.querySelectorAll('.v-price').forEach(el => { el.classList.toggle('v-price--sale', onSale); });
    root.querySelectorAll('[data-product-compare]').forEach(el => {
      el.hidden = !onSale;
      el.textContent = onSale ? money(variant.compare_at_price) : '';
    });
    const quantity = form?.querySelector('input[name="quantity"]');
    if (quantity && variant.quantity_rule) {
      const rule = variant.quantity_rule;
      const minimum = Number(rule.min) || 1;
      const increment = Number(rule.increment) || 1;
      const maximum = rule.max == null ? Infinity : Number(rule.max);
      quantity.min = String(minimum);
      quantity.step = String(increment);
      if (Number.isFinite(maximum)) quantity.max = String(maximum);
      else quantity.removeAttribute('max');
      const current = Number(quantity.value) || minimum;
      quantity.value = String(Math.min(maximum, Math.max(minimum, minimum + Math.floor((current - minimum) / increment) * increment)));
    }
    const featuredMedia = variantMediaFor(root)[String(variant.id)] || variant.featured_media?.id || variant.featured_image?.id;
    if (featuredMedia) showMedia(root, featuredMedia);
    else if (!root.querySelector('[data-media-id][hidden]')) {
      const first = root.querySelector('[data-media-id].is-active, [data-media-id]');
      if (first) showMedia(root, first.dataset.mediaId);
    }
    const isCurrentProduct = root.dataset.productUrl && new URL(root.dataset.productUrl, location.origin).pathname === location.pathname;
    if (updateUrl && (root.hasAttribute('data-update-url') || isCurrentProduct)) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url);
    }
    root.dispatchEvent(new CustomEvent('rove:variant:change', { bubbles: true, detail: { variant } }));
  }

  function syncDialogLock() {
    if (document.querySelector('dialog[open]')) {
      if (previousOverflow === undefined) previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
    } else if (previousOverflow !== undefined) {
      document.documentElement.style.overflow = previousOverflow;
      previousOverflow = undefined;
    }
  }

  function openDialog(trigger) {
    const dialog = document.getElementById(trigger.dataset.dialogOpen);
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (!dialog.open) {
      dialogTriggers.set(dialog, trigger);
      dialog.showModal();
      trigger.setAttribute('aria-expanded', 'true');
      syncDialogLock();
      const focus = dialog.querySelector('[autofocus], input[type="search"]');
      if (focus) focus.focus();
    }
  }

  function errorMessage(data, fallback) {
    if (typeof data?.description === 'string') return data.description;
    if (typeof data?.message === 'string') return data.message;
    if (data?.errors && typeof data.errors === 'object') {
      return Object.values(data.errors).flat().filter(value => typeof value === 'string').join(' ') || fallback;
    }
    return fallback;
  }

  async function refreshCartCount() {
    const response = await fetch(apiUrl(routes().cart_url || '/cart'), {
      headers: { Accept: 'application/json' }, credentials: 'same-origin'
    });
    if (!response.ok) return;
    const cart = await response.json();
    if (!Number.isFinite(Number(cart.item_count))) return;
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = String(cart.item_count);
      el.hidden = Number(cart.item_count) === 0;
    });
    document.dispatchEvent(new CustomEvent('rove:cart:updated', { detail: { cart } }));
  }

  async function addToCart(form) {
    if (busyForms.has(form)) return;
    const root = form.closest('[data-product-root]');
    const error = root?.querySelector('[data-product-error]');
    if (error) { error.textContent = ''; error.hidden = true; }
    const id = form.querySelector('[data-variant-id], [name="id"]');
    const variant = root ? selectedVariant(root) : null;
    if (!id?.value || id.disabled || (variant && !variant.available)) return;
    // Capture first: disabled submit controls should not change the submitted form data.
    const body = new FormData(form);
    busyForms.add(form);
    form.setAttribute('aria-busy', 'true');
    if (root) renderVariant(root);
    else form.querySelectorAll('[data-add-to-cart]').forEach(button => { button.disabled = true; });
    announce(text('adding', 'Adding…'));
    try {
      const response = await fetch(apiUrl(routes().cart_add_url || '/cart/add'), {
        method: 'POST', body, headers: { Accept: 'application/json' }, credentials: 'same-origin'
      });
      let data;
      try { data = await response.json(); } catch (_) { data = null; }
      if (!response.ok || !data || typeof data !== 'object') throw new Error(errorMessage(data, text('cartError', 'Unable to add this item. Please try again.')));
      announce(text('added', 'Added to your bag.'));
      if (root) root.querySelectorAll('[data-cart-confirmation]').forEach(el => { el.hidden = false; });
      document.dispatchEvent(new CustomEvent('rove:cart:added', { detail: { item: data } }));
      // A cart-count refresh failure must never invite the customer to add the item again.
      try { await refreshCartCount(); } catch (_) { /* The item was already added successfully. */ }
    } catch (failure) {
      const message = failure?.message || text('cartError', 'Unable to add this item. Please try again.');
      if (error) { error.textContent = message; error.hidden = false; }
      announce(message);
    } finally {
      busyForms.delete(form);
      form.removeAttribute('aria-busy');
      if (root) renderVariant(root);
      else form.querySelectorAll('[data-add-to-cart]').forEach(button => { button.disabled = false; });
    }
  }

  function predictiveElements(input) {
    const scope = input.closest('[data-predictive-search]') || input.closest('dialog') || input.closest('form');
    return { scope, results: scope?.querySelector('[data-predictive-results]') };
  }

  function clearPredictive(input, results) {
    if (results) { results.replaceChildren(); results.hidden = true; }
    input.setAttribute('aria-expanded', 'false');
  }

  function renderPredictive(input, results, data) {
    results.replaceChildren();
    const products = data?.resources?.results?.products || [];
    if (!products.length) { clearPredictive(input, results); return; }
    const list = document.createElement('ul');
    list.className = 'v-predictive-list';
    products.slice(0, 6).forEach(product => {
      let url;
      try { url = new URL(product.url, window.location.origin); } catch (_) { return; }
      if (url.origin !== window.location.origin || !/^https?:$/.test(url.protocol)) return;
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.className = 'v-predictive-item';
      link.href = url.href;
      const rawImage = product.featured_image?.url || product.image;
      if (typeof rawImage === 'string') {
        let imageUrl;
        try { imageUrl = new URL(rawImage, window.location.origin); } catch (_) { imageUrl = null; }
        if (imageUrl && /^https?:$/.test(imageUrl.protocol)) {
          const image = document.createElement('img');
          image.src = imageUrl.href;
          image.alt = '';
          image.width = 56;
          image.height = 56;
          image.loading = 'lazy';
          link.append(image);
        }
      }
      const title = document.createElement('span');
      title.textContent = product.title || '';
      link.append(title);
      item.append(link);
      list.append(item);
    });
    if (!list.childElementCount) { clearPredictive(input, results); return; }
    results.append(list);
    results.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  function schedulePredictive(input) {
    const { results } = predictiveElements(input);
    if (!results || !routes().predictive_search_url) return;
    const previous = searches.get(input);
    if (previous) { clearTimeout(previous.timer); previous.controller?.abort(); }
    const state = { timer: null, controller: null };
    searches.set(input, state);
    const query = input.value.trim();
    if (query.length < 2) { clearPredictive(input, results); return; }
    state.timer = window.setTimeout(async () => {
      state.controller = new AbortController();
      const url = apiUrl(routes().predictive_search_url, '.json');
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '6');
      try {
        const response = await fetch(url, {
          signal: state.controller.signal, headers: { Accept: 'application/json' }, credentials: 'same-origin'
        });
        if (!response.ok) throw new Error('Search unavailable');
        const data = await response.json();
        if (searches.get(input) === state && input.value.trim() === query) renderPredictive(input, results, data);
      } catch (failure) {
        if (failure.name !== 'AbortError' && searches.get(input) === state) clearPredictive(input, results);
      }
    }, 250);
  }

  function initializeModels(scope) {
    const models = [...scope.querySelectorAll('model-viewer')].filter(model => !modelViewers.has(model));
    if (!models.length || !window.Shopify?.loadFeatures) return;
    if (!modelFeature) {
      modelFeature = new Promise(resolve => {
        window.Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0', onLoad: errors => resolve(!errors) }]);
      });
      if (!document.querySelector('[data-model-viewer-style]')) {
        const stylesheet = document.createElement('link');
        stylesheet.rel = 'stylesheet';
        stylesheet.href = 'https://cdn.shopify.com/shopifycloud/model-viewer-ui/assets/v1.0/model-viewer-ui.css';
        stylesheet.dataset.modelViewerStyle = '';
        document.head.append(stylesheet);
      }
    }
    void modelFeature.then(loaded => {
      if (!loaded || !window.Shopify.ModelViewerUI) return;
      models.forEach(model => {
        if (!model.isConnected || modelViewers.has(model)) return;
        try {
          new window.Shopify.ModelViewerUI(model);
          modelViewers.add(model);
        } catch (_) { /* Shopify's poster remains available if 3D initialization fails. */ }
      });
    });
  }

  function initialize(scope = document) {
    scope.querySelectorAll('[data-product-root]').forEach(root => renderVariant(root));
    initializeModels(scope);
  }

  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const dialogLink = target.closest('dialog a[href]');
    if (dialogLink) dialogLink.closest('dialog').close();
    const opener = target.closest('[data-dialog-open]');
    if (opener) { event.preventDefault(); openDialog(opener); return; }
    const closer = target.closest('[data-dialog-close]');
    if (closer) { event.preventDefault(); closer.closest('dialog')?.close(); return; }
    if (target instanceof HTMLDialogElement && target.open) {
      const rect = target.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) target.close();
      return;
    }
    const mediaButton = target.closest('[data-media-target]');
    if (mediaButton) {
      const root = mediaButton.closest('[data-product-root]');
      if (root) { event.preventDefault(); showMedia(root, mediaButton.dataset.mediaTarget); }
      return;
    }
    const quantityButton = target.closest('[data-quantity-step]');
    if (quantityButton) {
      const input = quantityButton.closest('[data-quantity-control]')?.querySelector('input[type="number"]');
      if (!input || input.disabled) return;
      event.preventDefault();
      const step = Number(input.step) || 1;
      const minimum = input.min === '' ? 1 : Number(input.min);
      const maximum = input.max === '' ? Infinity : Number(input.max);
      const current = Number(input.value) || minimum;
      input.value = String(Math.min(maximum, Math.max(minimum, current + Number(quantityButton.dataset.quantityStep) * step)));
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  document.addEventListener('close', event => {
    if (!(event.target instanceof HTMLDialogElement)) return;
    const trigger = dialogTriggers.get(event.target);
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      if (trigger.isConnected) trigger.focus({ preventScroll: true });
    }
    syncDialogLock();
  }, true);

  document.addEventListener('change', event => {
    if (!(event.target instanceof Element)) return;
    if (event.target.matches('[data-option-index]')) {
      const root = event.target.closest('[data-product-root]');
      if (root) {
        const error = root.querySelector('[data-product-error]');
        if (error) { error.textContent = ''; error.hidden = true; }
        renderVariant(root, true);
      }
    }
  });

  document.addEventListener('input', event => {
    if (event.target instanceof HTMLInputElement && event.target.matches('[data-predictive-input]')) schedulePredictive(event.target);
  });

  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape' || !(event.target instanceof Element)) return;
    const scope = event.target.closest('[data-predictive-search], dialog');
    const input = scope?.querySelector('[data-predictive-input]');
    if (input) {
      const state = searches.get(input);
      if (state) { clearTimeout(state.timer); state.controller?.abort(); }
      clearPredictive(input, scope.querySelector('[data-predictive-results]'));
    }
  });

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-product-form]')) return;
    // Accelerated checkout and ordinary cart/update forms retain Shopify's native submission.
    const submitter = event.submitter;
    if (submitter && !submitter.matches('[data-add-to-cart]')) return;
    if (!form.reportValidity()) return;
    event.preventDefault();
    void addToCart(form);
  });

  document.addEventListener('shopify:section:load', event => { initialize(event.target); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
})();
